import { FlatPanelStateContext, getCurrentDetector } from 'app/components/FlatPanelStateProvider';
import { AcquisitionConstants } from 'app/interfaces/Image';
import fs from 'app/native/node/fs';
import path from 'app/native/node/path';
import XRayGeneratorHistoryContext, {
  XRayGeneratorHistory,
} from 'app/providers/XRayGeneratorHistoryProvider/context';
import { XRayGeneratorControllerContext } from 'app/providers/XRayGeneratorProvider';
import convertXRayFileToShotObjects from 'app/providers/convertXRayFileToShotObjects';
import { selectXRaySelectedOperators } from 'app/redux/xrayGeneratorConfiguration/reducer';
import { DETECTOR_EVENTS } from 'app/types/xray';
import { EVENT_GENERATOR_RAD_PARAMETERS_EXPOSURE } from 'app/xray/generator/constants';
import { format } from 'date-fns';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

type XRayGeneratorHistoryProviderProps = {
  xRayHistoryPath: string;
};

type ExposureEvent = {
  kV: number;
  mAs: number;
  mA: number;
  ms: number;
  focus: 0 | 1; // SMALL / BIG focus
  dose: number;
};

function formatDateToExcelCompatibleFormat(csvLine: string) {
  // Date Input format: yyyy-MM-dd_kk:mm:ss or yyyy-MM-dd kk:mm yyyy-MM-dd kk:mm:ss or
  // Date Output format: yyyy-MM-dd kk:mm
  const match = csvLine.match(/^(\d{4}-\d{2}-\d{2})(?:_| )(\d{2}:\d{2})(:\d{2})?(,.*)/);
  console.debug('formatDateToExcelCompatibleFormat', { csvLine, match });
  if (match) {
    return `${match[1]} ${match[2]}${match[4]}`;
  }
  return csvLine;
}

const getOperatorHistoryAsCsv = (fileContent: string, operator: string) => {
  const header = 'date,kV,mAs,mA,ms,operators\r\n';
  const matchingLines = fileContent
    .split(/\r?\n/)
    .filter((line) => !operator || line.match(new RegExp(`(,|")${operator}(,|")`)))
    .map(formatDateToExcelCompatibleFormat)
    .join('\r\n');
  return header + matchingLines;
};

const saveShotToHistory = async (
  xRayHistoryPath: string,
  { kV, mAs, ms, mA }: ExposureEvent,
  operators: string[] = []
) => {
  if (!xRayHistoryPath) return;
  const date = format(new Date(), 'yyyy-MM-dd kk:mm:ss');
  const operatorsString = operators.length > 0 ? `"${operators.join(',')}"` : '';
  const lineContent = [date, kV, mAs, mA, ms, operatorsString].join(',');
  await fs()?.promises.appendFile(xRayHistoryPath, `${lineContent}\r\n`, { encoding: 'utf8' });
};

const saveDetectorShotToHistory = async (
  xRayHistoryPath: string,
  { kV, s, mA }: AcquisitionConstants,
  operators: string[] = []
) => {
  if (!xRayHistoryPath) return;
  const date = format(new Date(), 'yyyy-MM-dd kk:mm:ss');
  const ms = s * 1000; // Convert seconds to milliseconds
  const mAs = mA * s;
  const operatorsString = operators.length > 0 ? `"${operators.join(',')}"` : '';
  const lineContent = [
    date,
    kV.toFixed(0),
    +mAs.toFixed(2),
    mA.toFixed(0),
    ms.toFixed(0),
    operatorsString,
  ].join(',');
  await fs()?.promises.appendFile(xRayHistoryPath, `${lineContent}\r\n`, { encoding: 'utf8' });
};

// async function harmonizeHistoryCsvDateTime(filePath: string) {
//   /*
//   Old format: yyyy-MM-dd_kk:mm:ss
//   New format: yyyy-MM-dd kk:mm
//   We are making this change to make it easier to import the history into Excel since Excel is not able to parse our custom date format.
//   */
//   try {
//     const content = fs().existsSync(filePath)
//       ? await fs().promises.readFile(filePath, 'utf8')
//       : undefined;
//     if (!content) return;

//     // Detect if the file has the old format (yyyy-MM-dd_kk:mm:ss)
//     const oldFormatRegex = /^\d{4}-\d{2}-\d{2}_\d{2}:\d{2}:\d{2}/;
//     if (!oldFormatRegex.test(content)) return; // No need to migrate

//     // Process each line and convert the date format
//     const lines = content.split(/\r?\n/);
//     const updatedLines = lines.map((line) => {
//       if (!line || line.startsWith('date,')) return line;

//       const match = line.match(/^(\d{4}-\d{2}-\d{2})_(\d{2}:\d{2}):\d{2}(.*)/);
//       if (match) {
//         // Replace yyyy-MM-dd_kk:mm:ss with yyyy-MM-dd kk:mm
//         return `${match[1]} ${match[2]}${match[3]}`;
//       }
//       return line;
//     });

//     // Write the updated content back to the file
//     await fs().promises.writeFile(filePath, updatedLines.join('\r\n'), 'utf8');
//   } catch (error) {
//     console.error('Error migrating X-Ray history file format:', error);
//   }
// }

async function harmonizeHistoryCsvDateTime(filePath: string) {
  /*
  v1 format: yyyy-MM-dd_kk:mm:ss
  v2 format: yyyy-MM-dd kk:mm
  We are making this change to make it easier to import the history into Excel since Excel is not able to parse our custom date format.
  v3 format: yyyy-MM-dd kk:mm:ss
  We finally go back to second precision since it is needed for error interlacing, excel compatibility will be handled by extractor function.
  */
  try {
    const content = fs().existsSync(filePath)
      ? await fs().promises.readFile(filePath, 'utf8')
      : undefined;
    if (!content) return;

    // Detect if the file has the old format (yyyy-MM-dd_kk:mm:ss)
    const minutePrecisionFormat = /^\d{4}-\d{2}-\d{2}(?:_| )\d{2}:\d{2},/;
    if (!minutePrecisionFormat.test(content)) return; // No need to migrate, already to second precision

    // Process each line and convert the date format
    const lines = content.split(/\r?\n/);
    const updatedLines = lines.map((line) => {
      if (!line || line.startsWith('date,')) return line;

      const match = line.match(/^(\d{4}-\d{2}-\d{2})(?:_| )(\d{2}:\d{2})(:\d{2})?(,.*)/);
      if (match) {
        // Replace yyyy-MM-dd_kk:mm:ss with yyyy-MM-dd kk:mm
        return `${match[1]} ${match[2]}${match[3] || ':00'}${match[4]}`; // Adding seconds as :00 since we are going back to second precision in the format
      }
      return line;
    });

    // Write the updated content back to the file
    await fs().promises.writeFile(filePath, updatedLines.join('\r\n'), 'utf8');
  } catch (error) {
    console.error('Error migrating X-Ray history file format:', error);
  }
}

export default function XRayGeneratorHistoryProvider({
  children,
  xRayHistoryPath,
}: React.PropsWithChildren<XRayGeneratorHistoryProviderProps>) {
  const generator = useContext(XRayGeneratorControllerContext);
  const flatPanelContext = useContext(FlatPanelStateContext);
  const detector = getCurrentDetector(flatPanelContext);
  const [acquisitionConstants, setAcquisitionConstants] = useState<AcquisitionConstants>(undefined);
  const acquisitionConstantsRef = useRef<AcquisitionConstants>();
  acquisitionConstantsRef.current = acquisitionConstants;
  const selectedOperators = useSelector(selectXRaySelectedOperators);
  const selectedOperatorsRef = useRef<string[]>();
  selectedOperatorsRef.current = selectedOperators;
  const xRayHistoryPathRef = useRef<string>();
  xRayHistoryPathRef.current = xRayHistoryPath;

  useEffect(() => {
    if (!xRayHistoryPath) return undefined;

    (async () => {
      await fs()?.promises.mkdir(path().dirname(xRayHistoryPath), { recursive: true });
      await harmonizeHistoryCsvDateTime(xRayHistoryPath);
    })();
    return undefined;
  }, [xRayHistoryPath]);

  const xRayGeneratorHistory: XRayGeneratorHistory = useMemo(
    () => ({
      getHistoryAsCsv: (operator: string) =>
        fs()
          ?.promises.readFile(xRayHistoryPath, { encoding: 'utf8' })
          .then((fileContent: string) => getOperatorHistoryAsCsv(fileContent, operator))
          .catch(() => 'date,kV,mAs,mA,ms,operators\r\n'),
      getAllHistory: () =>
        fs()
          ?.promises.readFile(xRayHistoryPath, { encoding: 'utf8' })
          .then(convertXRayFileToShotObjects)
          .catch(() => []),
      setAcquisitionConstants,
    }),
    [setAcquisitionConstants]
  );

  useEffect(() => {
    const saveGeneratorShotToHistory = (exposureEvent: ExposureEvent) => {
      saveShotToHistory(
        xRayHistoryPathRef.current,
        exposureEvent,
        // @ts-ignore
        selectedOperatorsRef.current
      );
    };
    generator?.on(EVENT_GENERATOR_RAD_PARAMETERS_EXPOSURE, saveGeneratorShotToHistory);
    return () =>
      generator?.removeEventListener(
        EVENT_GENERATOR_RAD_PARAMETERS_EXPOSURE,
        // @ts-ignore
        saveGeneratorShotToHistory
      );
  }, [generator]);

  useEffect(() => {
    if (generator) return undefined;

    const saveToHistoryOnNewImage = () => {
      saveDetectorShotToHistory(
        xRayHistoryPathRef.current,
        acquisitionConstantsRef.current,
        // @ts-ignore
        selectedOperatorsRef.current
      );
    };

    detector?.on(DETECTOR_EVENTS.NEW_IMAGE, saveToHistoryOnNewImage);
    return () =>
      detector?.removeListener(
        DETECTOR_EVENTS.NEW_IMAGE,
        // @ts-ignore
        saveToHistoryOnNewImage
      );
  }, [detector, generator]);

  return (
    <XRayGeneratorHistoryContext.Provider value={xRayGeneratorHistory}>
      {children}
    </XRayGeneratorHistoryContext.Provider>
  );
}
